import type { Project, ProjectManifest, Photo } from "../types/project";

// Cache for loaded projects
let cachedProjects: Project[] | null = null;
let cachedFullManifest: ProjectManifest | null = null;
let cachedRanking: { topImages: string[] } | null = null;
let cacheTimestamp: number = 0;
const CACHE_DURATION = 60000; // 1 minute

// Share project identifiers
const SHARE_PREFIX = 'share-';
const SHARE_PATTERN = /^share-|_share$/;

// Check if a project is a share project
export function isShareProject(projectName: string): boolean {
  return projectName.startsWith(SHARE_PREFIX) || SHARE_PATTERN.test(projectName);
}

// Get the original project name from a share project name
export function getOriginalProjectName(shareName: string): string {
  return shareName.replace(SHARE_PREFIX, '').replace(/_share$/, '');
}

// Load the full manifest (includes all projects)
async function loadFullManifest(): Promise<ProjectManifest | null> {
  try {
    const manifestResponse = await fetch("/optimized/project-groups.json");
    if (!manifestResponse.ok) {
      console.warn("Could not load project groups manifest");
      return null;
    }
    const manifest: ProjectManifest = await manifestResponse.json();
    cachedFullManifest = manifest;
    return manifest;
  } catch (error) {
    console.error("Failed to load manifest:", error);
    return null;
  }
}

// Load project groups from the generated manifest
export async function loadProjects(): Promise<Project[]> {
  const now = Date.now();
  if (cachedProjects && now - cacheTimestamp < CACHE_DURATION) {
    return cachedProjects;
  }

  try {
    const manifest = await loadFullManifest();
    if (!manifest) {
      console.warn("Could not load project groups, using fallback data");
      return getFallbackProjects();
    }

    // Extract ranking from the manifest
    if (manifest.ranking) {
      cachedRanking = manifest.ranking;
      console.log(
        `📋 Loaded ranking with ${cachedRanking.topImages.length} images from manifest`
      );
    } else {
      cachedRanking = { topImages: [] };
      console.log("📋 No ranking found in manifest");
    }

    // Filter out share projects from the main list - they'll be handled separately
    const mainProjects = manifest.projects.filter(
      p => !isShareProject(p.name) && !p.path.includes('share-')
    );

    cachedProjects = mainProjects;
    cacheTimestamp = now;

    console.log(`📁 Loaded ${mainProjects.length} main projects`);
    return cachedProjects;
  } catch (error) {
    console.error("Failed to load projects:", error);
    return getFallbackProjects();
  }
}

// Get the base filename without extension and without leading underscore
function getBaseFilename(filename: string): string {
  const name = filename.replace(/\.[^.]+$/, "");
  return name.replace(/^_+/, "");
}

// Get the optimized filename for a specific width
function getOptimizedFilename(
  originalFilename: string,
  width: number,
  format: string = "webp"
): string {
  const baseName = getBaseFilename(originalFilename);
  return `${baseName}-${width}w.${format}`;
}

// Convert project images to Photo format for the masonry grid
export function projectToPhotos(project: Project): Photo[] {
  return project.images.map((image, index) => {
    const baseUrl = "/optimized";
    // For share projects, we need to use the actual project path
    const isShare = isShareProject(project.name);
    const projectPath = project.name !== "root" && !isShare
      ? `${project.path}/`
      : isShare
        ? `${project.path}/`
        : "";
    const format = "webp";

    const baseName = getBaseFilename(image.filename);

    let srcSet = "";
    const optimizedWebp = image.optimized?.webp;
    if (optimizedWebp) {
      const srcSetParts: string[] = [];
      const widths = Object.keys(optimizedWebp);
      for (const width of widths) {
        const fullPath = optimizedWebp[width];
        const filename = fullPath.split(/[\\/]/).pop() || "";
        srcSetParts.push(`${baseUrl}/${projectPath}${filename} ${width}w`);
      }
      srcSet = srcSetParts.join(", ");
    } else {
      const breakpoints = [400, 1200];
      srcSet = breakpoints
        .map((bp) => {
          const filename = getOptimizedFilename(image.filename, bp, format);
          return `${baseUrl}/${projectPath}${filename} ${bp}w`;
        })
        .join(", ");
    }

    let src = "";
    let largestWidth = 1200;
    const optimizedWebpForSrc = image.optimized?.webp;
    if (optimizedWebpForSrc) {
      const widths = Object.keys(optimizedWebpForSrc).map((w) => parseInt(w));
      if (widths.length > 0) {
        largestWidth = Math.max(...widths);
        const fullPath = optimizedWebpForSrc[largestWidth.toString()];
        const filename = fullPath.split(/[\\/]/).pop() || "";
        src = `${baseUrl}/${projectPath}${filename}`;
      }
    }

    if (!src) {
      const filename = getOptimizedFilename(
        image.filename,
        largestWidth,
        format
      );
      src = `${baseUrl}/${projectPath}${filename}`;
    }

    const title = image.filename
      .replace(/\.[^.]+$/, "")
      .replace(/^_+/, "")
      .replace(/[_-]/g, " ")
      .replace(/\b\w/g, (l) => l.toUpperCase())
      .trim();

    const displayName = isShare
      ? getOriginalProjectName(project.name).replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
      : project.name === "root"
        ? "General"
        : project.name
            .replace(/-/g, " ")
            .replace(/\b\w/g, (l) => l.toUpperCase());

    return {
      id: `${project.name}-${index}`,
      src: src,
      srcSet: srcSet,
      alt: title,
      title: title,
      category: displayName,
      width: image.width || 800,
      height: image.height || 600,
      aspectRatio:
        image.width && image.height ? image.width / image.height : 4 / 3,
      projectName: project.name,
      project: project.path,
      filename: image.filename,
      isShareProject: isShare
    };
  });
}

// Fallback data if manifest can't be loaded
function getFallbackProjects(): Project[] {
  return [
    {
      name: "root",
      path: "",
      imageCount: 0,
      coverImage: null,
      images: []
    }
  ];
}

// Get all photos from all projects (excluding root and share projects)
export async function getAllPhotos(): Promise<Photo[]> {
  const projects = await loadProjects();
  const allPhotos: Photo[] = [];

  for (const project of projects) {
    // Skip root and share projects for the main portfolio
    if (project.name === "root" || isShareProject(project.name)) continue;
    const photos = projectToPhotos(project);
    allPhotos.push(...photos);
  }

  return allPhotos;
}

// Get photos from a specific project (including share projects via direct access)
export async function getProjectPhotos(projectName: string): Promise<Photo[]> {
  // First, try to find in cached main projects
  const projects = await loadProjects();
  let project = projects.find((p) => p.name === projectName);

  // If not found in main projects, check the full manifest for share projects
  if (!project) {
    try {
      const manifest = await loadFullManifest();
      if (manifest) {
        // Try exact match first
        project = manifest.projects.find((p) => p.name === projectName);

        // If not found, try with share prefix
        if (!project && !projectName.startsWith(SHARE_PREFIX)) {
          const shareName = `${SHARE_PREFIX}${projectName}`;
          project = manifest.projects.find((p) => p.name === shareName);
        }

        // If still not found, try case-insensitive
        if (!project) {
          project = manifest.projects.find(
            (p) => p.name.toLowerCase() === projectName.toLowerCase()
          );
        }

        // If found in manifest, convert to photos
        if (project) {
          console.log(`📸 Found share project: ${project.name}`);
          return projectToPhotos(project);
        }
      }
    } catch (error) {
      console.warn("Could not load full manifest for share project:", error);
    }
  }

  if (!project) {
    console.warn(`Project "${projectName}" not found`);
    return [];
  }

  return projectToPhotos(project);
}

// Get the most recent projects (for the homepage - excludes share projects)
export async function getRecentProjects(limit: number = 3): Promise<Project[]> {
  const projects = await loadProjects();
  const filtered = projects.filter((p) => p.name !== "root" && !isShareProject(p.name));
  return filtered.slice(0, limit);
}

// Get project by name (includes share projects via direct access)
export async function getProjectByName(
  projectName: string
): Promise<Project | null> {
  const projects = await loadProjects();

  // Try exact match
  let project = projects.find((p) => p.name === projectName);

  // If not found, try case-insensitive
  if (!project) {
    project = projects.find(
      (p) => p.name.toLowerCase() === projectName.toLowerCase()
    );
  }

  // If still not found, check full manifest for share projects
  if (!project) {
    try {
      const manifest = await loadFullManifest();
      if (manifest) {
        // Try exact match
        project = manifest.projects.find((p) => p.name === projectName);

        // Try with share prefix
        if (!project && !projectName.startsWith(SHARE_PREFIX)) {
          const shareName = `${SHARE_PREFIX}${projectName}`;
          project = manifest.projects.find((p) => p.name === shareName);
        }
      }
    } catch (error) {
      console.warn("Could not load full manifest for share project:", error);
    }
  }

  return project || null;
}

// Get project cover image URL
export function getProjectCoverUrl(project: Project): string | null {
  if (!project.coverImage) return null;

  const baseUrl = "/optimized";
  const isShare = isShareProject(project.name);
  const projectPath = project.name !== "root" && !isShare
    ? `${project.path}/`
    : isShare
      ? `${project.path}/`
      : "";
  const format = "webp";

  const coverImage = project.images.find(
    (img) => img.filename === project.coverImage?.filename
  );
  if (coverImage && coverImage.optimized?.webp) {
    const widths = Object.keys(coverImage.optimized.webp);
    if (widths.length > 0) {
      const largestWidth = Math.max(...widths.map((w) => parseInt(w)));
      const fullPath = coverImage.optimized.webp[largestWidth.toString()];
      const filename = fullPath.split(/[\\/]/).pop() || "";
      return `${baseUrl}/${projectPath}${filename}`;
    }
  }

  const baseName = getBaseFilename(project.coverImage.filename);
  return `${baseUrl}/${projectPath}${baseName}-1200w.${format}`;
}

// Get the top ranked images (excludes share projects)
export async function getTopRankedImages(limit?: number): Promise<Photo[]> {
  // First, ensure projects are loaded and ranking is cached
  await loadProjects();

  // Get all photos (excluding share projects)
  const allPhotos = await getAllPhotos();

  // Get the ranking list from the cached manifest
  const rankingList = cachedRanking?.topImages || [];

  // If no ranking, return all photos (or limited if specified)
  if (rankingList.length === 0) {
    console.log("📋 No ranking found, returning all photos");
    return limit ? allPhotos.slice(0, limit) : allPhotos;
  }

  console.log(`📋 Using ranking with ${rankingList.length} images`);

  // Create a map of filename to photo for quick lookup
  const photoMap = new Map<string, Photo>();
  for (const photo of allPhotos) {
    if (photo.filename) {
      photoMap.set(photo.filename, photo);
    }
    const baseName = getBaseFilename(photo.filename || "");
    photoMap.set(baseName, photo);
  }

  // Build the ordered list based on the ranking
  const rankedPhotos: Photo[] = [];
  const usedIds = new Set<string>();

  for (const rankedFilename of rankingList) {
    let foundPhoto = photoMap.get(rankedFilename);
    if (!foundPhoto) {
      const baseName = getBaseFilename(rankedFilename);
      foundPhoto = photoMap.get(baseName);
    }
    if (foundPhoto && !usedIds.has(foundPhoto.id)) {
      rankedPhotos.push(foundPhoto);
      usedIds.add(foundPhoto.id);
    } else if (!foundPhoto) {
      console.warn(`⚠️  Ranked image not found: ${rankedFilename}`);
    }
  }

  // Add any remaining photos that weren't in the ranking
  const remainingPhotos: Photo[] = [];
  for (const photo of allPhotos) {
    if (!usedIds.has(photo.id)) {
      remainingPhotos.push(photo);
    }
  }

  // Combine: ranked photos first (preserving order), then remaining
  let sortedPhotos = [...rankedPhotos, ...remainingPhotos];

  // Apply limit if specified
  if (limit && limit > 0) {
    sortedPhotos = sortedPhotos.slice(0, limit);
  }

  console.log(
    `📋 Returning ${sortedPhotos.length} photos (${
      rankedPhotos.length
    } ranked, ${remainingPhotos.length} unranked)${
      limit ? ` (limited to ${limit})` : " (all)"
    }`
  );

  return sortedPhotos;
}

// Clear cache (useful for development)
export function clearProjectCache(): void {
  cachedProjects = null;
  cachedFullManifest = null;
  cachedRanking = null;
  cacheTimestamp = 0;
}
